import React from 'react';
import { useNavigate, useParams } from 'react-router';
import type { AccountBenchCandidate, Opportunity } from '@axiom/validation';
import { AccountConst } from '@axiom/const';
import type { BadgeBackgroundType } from '@axiom/ui';
import {
  Gutter,
  CandidateProfileUtil,
  ImageCircle,
  LayoutItem,
  Badge,
  Layout,
  ParagraphHeader,
  Tiles,
  TilesItem,
} from '@axiom/ui';

const { CandidateRelationships } = AccountConst;

type CandidateRelationship =
  (typeof CandidateRelationships)[keyof typeof CandidateRelationships];

const relationshipConfig: Record<
  CandidateRelationship,
  { label: string; background: BadgeBackgroundType }
> = {
  [CandidateRelationships.CurrentlyEngaged]: {
    label: 'Engaged',
    background: 'navy blue',
  },
  [CandidateRelationships.PreviouslyEngaged]: {
    label: 'Prev Engaged',
    background: 'blue',
  },
  [CandidateRelationships.PreviouslyInterviewing]: {
    label: 'Interviewed',
    background: 'sky',
  },
  [CandidateRelationships.PreviouslySubmitted]: {
    label: 'Submitted',
    background: 'light gray',
  },
  [CandidateRelationships.PreviouslyEmployed]: {
    label: 'Prev Employed',
    background: 'light gray',
  },
};

export type OpportunityTalentClientBenchListProps = {
  opportunity: Opportunity;
  benchCandidates: AccountBenchCandidate[];
};

export const OpportunityTalentClientBenchList = ({
  opportunity,
  benchCandidates,
}: OpportunityTalentClientBenchListProps) => {
  const navigate = useNavigate();
  const params = useParams();

  return (
    <Tiles columnWidth={1} name="client-bench-list">
      {benchCandidates.map(candidate => (
        <TilesItem
          key={candidate.candidateId}
          name={candidate.candidateId}
          selected={params.viewId === candidate.candidateId}
          onClick={() => {
            void navigate(
              `/opportunity-detail/${opportunity.id}/talent/${candidate.candidateId}`
            );
          }}
          Badge={
            <Gutter right="8px">
              <Badge
                name={`${candidate.candidateId}-relationship-badge`}
                background={
                  relationshipConfig[candidate.accountRelationship].background
                }
              >
                {relationshipConfig[candidate.accountRelationship].label}
              </Badge>
            </Gutter>
          }
        >
          <Layout position="top space-between">
            <Layout position="middle">
              <LayoutItem rightGutter="8px">
                <ImageCircle
                  name={`${candidate.candidateId}-image`}
                  size="small"
                  src={CandidateProfileUtil.getProfileImageUri(
                    {
                      profileImageKey: candidate.profileImageKey,
                    },
                    'W_56'
                  )}
                  imageName={candidate.calculatedDisplayName}
                />
              </LayoutItem>
              <ParagraphHeader maxLines={1} name="candidate-name">
                {candidate.calculatedDisplayName}
              </ParagraphHeader>
            </Layout>
          </Layout>
        </TilesItem>
      ))}
    </Tiles>
  );
};
